import { bus } from './bus';
import { S } from './game';
import { m } from '../i18n';
import { clamp100, CHORES, WATER_MAX, type AffectionKey, type ChoreKey, type ItemKey, type StatKey } from './state';
import { sfx } from '../audio/sfx';

export interface Delta {
  energy?: number;
  mood?: number;
  study?: number;
  bladder?: number;
  stars?: number;
  coins?: number;
  /** Game minutes spent on the action. */
  minutes?: number;
  love?: Partial<Record<AffectionKey, number>>;
}

const FLOAT_COLORS = {
  stars: '#ffd84a',
  coins: '#f5b041',
  energy: '#ffb347',
  mood: '#5ee08a',
  study: '#74c0ff',
  love: '#ff7eb6',
};

/**
 * Apply a change to the game state. Every meter is clamped, stars/coins never go
 * negative, and positive gains are announced as floating text over the player.
 */
export function apply(d: Delta, opts: { float?: boolean } = {}): void {
  const s = S();
  const showFloat = opts.float ?? true;
  const floats: { text: string; color: string }[] = [];
  const r = m().rewards;

  for (const key of ['energy', 'mood', 'study', 'bladder'] as StatKey[]) {
    const v = d[key];
    if (!v) continue;
    s.stats[key] = clamp100(s.stats[key] + v);
    if (v > 0 && key !== 'bladder') floats.push({ text: r[key](v), color: FLOAT_COLORS[key] });
  }
  if (d.stars) {
    s.stars = Math.max(0, s.stars + d.stars);
    if (d.stars > 0) floats.push({ text: r.stars(d.stars), color: FLOAT_COLORS.stars });
  }
  if (d.coins) {
    s.coins = Math.max(0, s.coins + d.coins);
    if (d.coins > 0) floats.push({ text: r.coins(d.coins), color: FLOAT_COLORS.coins });
  }
  if (d.love) {
    for (const [k, v] of Object.entries(d.love) as [AffectionKey, number][]) {
      s.affection[k] = clamp100(s.affection[k] + v);
      if (v > 0 && k === 'mom') floats.push({ text: r.love(v), color: FLOAT_COLORS.love });
    }
  }
  if (d.minutes) s.time += d.minutes;

  if (showFloat) floats.forEach((f) => bus.emit('float', f));
  bus.emit('change');
}

/** True the first time `key` is claimed today; afterwards false. */
export function onceToday(key: string): boolean {
  const s = S();
  if (s.daily[key]) return false;
  s.daily[key] = true;
  return true;
}

/** True the first time `key` is claimed in this save. */
export function onceForever(key: string): boolean {
  const s = S();
  if (s.forever[key]) return false;
  s.forever[key] = true;
  return true;
}

export function counter(key: string): number {
  return S().counters[key] ?? 0;
}

export function bump(key: string, by = 1): number {
  const s = S();
  s.counters[key] = (s.counters[key] ?? 0) + by;
  return s.counters[key];
}

export function has(item: ItemKey, n = 1): boolean {
  return S().bag[item] >= n;
}

export function give(item: ItemKey, n = 1): void {
  const s = S();
  s.bag[item] = Math.max(0, s.bag[item] + n);
  if (item === 'water') s.bag.water = Math.min(WATER_MAX, s.bag.water);
  if (n > 0) bus.emit('float', { text: m().rewards.got(`${ITEM_ICONS[item]}×${n}`), color: '#ffe9a8' });
  bus.emit('change');
}

export function take(item: ItemKey, n = 1): boolean {
  if (!has(item, n)) return false;
  S().bag[item] -= n;
  bus.emit('change');
  return true;
}

export const ITEM_ICONS: Record<ItemKey, string> = {
  tea: '🍵',
  orange: '🍊',
  candy: '🍬',
  cookies: '🍪',
  cake: '🍰',
  strawberries: '🍓',
  book: '📖',
  water: '💧',
  riceBall: '🍙',
  juice: '🧃',
  atlas: '🗺️',
  kite: '🪁',
  kitten: '🐱',
  letter: '✉️',
  flowers: '💐',
  scarf: '🧣',
  keychain: '🔑',
  toy: '🚗',
  sandwich: '🥪',
  cocoa: '☕',
  umbrella: '☂️',
  tomato: '🍅',
  sunflower: '🌻',
  chiffon: '🎂',
  postcard: '🖼️',
  pineappleCake: '🍍',
  mochi: '🍡',
  pebble: '🪨',
  photo: '📸',
};

export function toast(text: string): void {
  bus.emit('toast', text);
}

export function burst(kind: 'sparkle' | 'heart' | 'note' | 'drop', count?: number): void {
  bus.emit('burst', { kind, count });
}

/** Mark a home chore as done; the full set unlocks a one-off daily reward. */
export function doChore(key: ChoreKey): boolean {
  const s = S();
  if (s.chores[key]) return false;
  s.chores[key] = true;
  bus.emit('change');
  return true;
}

export function allChoresDone(): boolean {
  const s = S();
  return CHORES.every((c) => s.chores[c]);
}

/** Positive feedback helper: sound + sparkles. */
export function celebrate(big = false): void {
  sfx(big ? 'fanfare' : 'ok');
  burst(big ? 'sparkle' : 'sparkle', big ? 18 : 8);
}
